
import { ChangeDetectionStrategy, Component, Injector, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { getRedirectUrl } from '@abp/ng.account';
import { AccountService } from '@abp/ng.account.core/proxy';
import {
  AuthService,
  ConfigStateService,
  CoreModule,
  EnvironmentService,
  SessionStateService,
} from '@abp/ng.core';
import { getPasswordValidators, ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { TermsService } from '../terms.service';

const { maxLength, required, requiredTrue, email } = Validators;

/**
 * Local replacement for ABP's register screen, registered against
 * `eAccountComponents.Register`. Identical to the original except that it will not submit
 * until the terms have been accepted, and it tells the server which version was accepted.
 *
 * Replacing the screen is only half of the guarantee -- `/api/account/register` is a real
 * endpoint that anything can post to, so the backend refuses a registration without
 * acceptance regardless of which client sent it. This component exists so that a person
 * sees the document and makes the choice, not so that the rule is enforced here.
 */
@Component({
    selector: 'app-register',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CoreModule, ThemeSharedModule, ReactiveFormsModule, RouterModule],
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {
  form!: UntypedFormGroup;
  // Signals, not plain fields: ABP renders this page inside its OnPush
  // `abp-replaceable-route-container`, so a field set in a subscribe is not redrawn (#34).
  readonly inProgress = signal(false);
  isSelfRegistrationEnabled = true;

  /**
   * The version the server says is in force, quoted back on submit. Null until it arrives;
   * the form stays disabled until then, because registering without it would mean recording
   * an acceptance of nothing in particular.
   */
  readonly termsVersion = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private accountService: AccountService,
    private configState: ConfigStateService,
    private toasterService: ToasterService,
    private authService: AuthService,
    private termsService: TermsService,
    private environment: EnvironmentService,
    private sessionState: SessionStateService,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.init();
    this.buildForm();
    this.fetchTermsVersion();
  }

  /**
   * The terms page for the language the visitor is actually reading. The documents are
   * separate Razor pages per language on the API host rather than one localized page, so the
   * language picks the URL instead of a header.
   */
  get termsUrl(): string {
    return `${this.apiUrl}/${this.documentLanguage}/Terms`;
  }

  get privacyUrl(): string {
    return `${this.apiUrl}/${this.documentLanguage}/PrivacyPolicy`;
  }

  get canSubmit(): boolean {
    return !!this.termsVersion();
  }

  protected init(): void {
    this.isSelfRegistrationEnabled =
      (this.configState.getSetting('Abp.Account.IsSelfRegistrationEnabled') || '').toLowerCase() !==
      'false';

    if (!this.isSelfRegistrationEnabled) {
      this.toasterService.warn(
        {
          key: 'AbpAccount::SelfRegistrationDisabledMessage',
          defaultValue: 'Self registration is disabled.',
        },
        '',
        { life: 10000 }
      );
    }
  }

  protected buildForm(): void {
    this.form = this.fb.group({
      username: ['', [required, maxLength(255)]],
      password: ['', [required, ...getPasswordValidators(this.injector)]],
      email: ['', [required, email]],
      // requiredTrue rather than required: an unticked checkbox reports a value of false,
      // which `required` accepts as present.
      acceptedTerms: [false, [requiredTrue]],
    });
  }

  protected fetchTermsVersion(): void {
    this.termsService.getCurrent().subscribe({
      next: terms => this.termsVersion.set(terms.version),
      error: () =>
        this.toasterService.error('::TermsUnavailable', '', { life: 7000 }),
    });
  }

  onSubmit(): void {
    if (this.form.invalid || !this.termsVersion()) {
      return;
    }

    this.inProgress.set(true);

    const newUser = {
      userName: this.form.get('username')?.value,
      password: this.form.get('password')?.value,
      emailAddress: this.form.get('email')?.value,
      appName: 'Angular',
      // Carried on the registration itself so the acceptance and the account are written in
      // one transaction. A separate call afterwards could fail and leave an account with
      // nothing on file, which is the state this change exists to make impossible.
      extraProperties: {
        AcceptedTermsVersion: this.termsVersion(),
      },
    };

    this.accountService
      .register(newUser)
      .pipe(
        switchMap(() =>
          this.authService.login({
            username: newUser.userName,
            password: newUser.password,
            redirectUrl: getRedirectUrl(this.injector),
          })
        ),
        catchError(err => {
          this.toasterService.error(
            err.error?.error_description ||
              err.error?.error?.message ||
              'AbpAccount::DefaultErrorMessage',
            '',
            { life: 7000 }
          );
          return throwError(() => err);
        }),
        finalize(() => this.inProgress.set(false))
      )
      .subscribe();
  }

  private get apiUrl(): string {
    return (this.environment.getApiUrl('default') || '').replace(/\/$/, '');
  }

  private get documentLanguage(): string {
    return (this.sessionState.getLanguage() || 'en').toLowerCase().startsWith('ar') ? 'Ar' : 'En';
  }
}
